import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, SlidersHorizontal } from "lucide-react";
import { useSilver } from "@/context/SilverContext";
import { AudienceHeadline } from "@/components/silver/results/AudienceHeadline";
import { CompositionPanel } from "@/components/silver/results/CompositionPanel";
import { GeoBreakdown } from "@/components/silver/results/GeoBreakdown";
import { ProfilesRail } from "@/components/silver/results/ProfilesRail";
import { ProfileDrawer } from "@/components/silver/results/ProfileDrawer";
import { PostcodeChip } from "@/components/silver/tool/PostcodeSearch";
import { LogicSummary } from "@/components/silver/tool/LogicSummary";
import { RollingNumber } from "@/components/silver/common/RollingNumber";
import { fmtInt } from "@/components/silver/common/AnimatedNumber";
import { formatGBP } from "@/config/pricing";

function PatchBar() {
  const { areas, removeArea, setFilterDrawerOpen, resetAll } = useSilver();
  return (
    <motion.div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} data-testid="patch-bar">
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="eyebrow-muted mr-1">Your patch</span>
          <AnimatePresence initial={false}>
            {areas.map((c) => <PostcodeChip key={c} code={c} onRemove={removeArea} size="sm" />)}
          </AnimatePresence>
        </div>
        <LogicSummary compact />
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        <button type="button" className="btn-ghost !py-2.5 text-[13px]" onClick={() => setFilterDrawerOpen(true)} data-testid="refine-audience-btn"><SlidersHorizontal className="h-4 w-4" /> Refine audience</button>
        <button type="button" className="btn-ghost !py-2.5 text-[13px]" onClick={resetAll} data-testid="start-again-btn"><RotateCcw className="h-4 w-4" /> Start again</button>
      </div>
    </motion.div>
  );
}

function NoMatch() {
  const { resetFilters } = useSilver();
  return (
    <div className="glass p-10 text-center" data-testid="results-no-match">
      <p className="text-2xl font-bold text-white">No matching households were found using these filters.</p>
      <p className="mt-2 text-white/55">Try fewer criteria or add more districts.</p>
      <button type="button" className="btn-primary mt-6" onClick={resetFilters} data-testid="results-broaden-btn">Broaden audience</button>
    </div>
  );
}

// Persistent next step. The campaign builder opens as its own full-screen stage.
function ActionBar() {
  const { audience, price, setPhase, goBack } = useSilver();
  return (
    <motion.div className="fixed inset-x-3 bottom-4 z-40 flex justify-center sm:inset-x-0" initial={{ y: 90, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 90, opacity: 0, transition: { duration: 0.2 } }} transition={{ type: "spring", stiffness: 260, damping: 28, delay: 0.6 }} data-testid="results-action-bar">
      <div className="glass-strong flex w-full max-w-[760px] items-center justify-between gap-3 !rounded-full p-2 sm:pl-3">
        <button type="button" className="btn-ghost !px-4 !py-3 text-[13px]" onClick={() => goBack("define")} data-testid="action-bar-back-btn">
          <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Audience setup</span><span className="sm:hidden">Back</span>
        </button>
        <p className="hidden min-w-0 text-[13px] text-white/60 md:block">
          <span className="num font-semibold text-white">{fmtInt(audience.total)}</span> households · from <RollingNumber value={price.baseCost} format={formatGBP} className="font-semibold text-[#C6EE5A]" />
        </p>
        <button type="button" className="btn-primary !px-6 !py-3 text-[14px]" onClick={() => setPhase("campaign")} data-testid="build-campaign-btn">
          Build campaign <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
}

export default function ResultsDashboard() {
  const { audience } = useSilver();
  const headingRef = useRef(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => headingRef.current?.focus({ preventScroll: true }), 900);
    return () => clearTimeout(t);
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }} transition={{ duration: 0.6 }} className="pb-24" data-testid="results-dashboard">
      <PatchBar />
      <div className="mt-10 space-y-8">
        {audience.total === 0 ? (
          <NoMatch />
        ) : (
          <>
            <AudienceHeadline ref={headingRef} />
            <CompositionPanel />
            <GeoBreakdown />
            <ProfilesRail onView={setProfile} />
          </>
        )}
      </div>
      <AnimatePresence>{audience.total > 0 && <ActionBar key="bar" />}</AnimatePresence>
      <ProfileDrawer code={profile} onClose={() => setProfile(null)} />
    </motion.div>
  );
}
